import { useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { AxiosError, axiosInstance, useFetchRows } from "@follosoft/react-components";

import {
  FlowPageInterface,
  OfflineSubscribePayload,
  PaymentPlanResource,
  PromotionResource,
} from "@/interfaces";
import { localizedRebateTypeName } from "@/utils/rebate";
import { forfeitsPromotionDiscount, planMissesPromotionDeadline, planFirstPaymentDate } from "@/utils/promotion-deadline";
import { paysInFullNow } from "@/utils/pay-in-full";
import { buildPriceLadder } from "@/utils/price-ladder";
import { useFlow } from "@/contexts/FlowContext.tsx";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@/utils/currency";
import { useContract } from "@/hooks/useContract";
import { AppDialog, AppDialogProps } from "./dialogs/AppDialog";
import { AlertCircle, Loader, RefreshCcw } from "lucide-react";
import { FlowPagePaymentMethodAccordion } from "./FlowPagePaymentMethodAccordion";
import PaymentMethodAccordionSkeleton from "./payment-method-accordion-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import {ContactUs} from "@/components/ContactUs.tsx";
import PaymentAgreementEmptyState from "@/components/payment-agreement-empty-state.tsx";
import PaymentAgreementSettledState from "@/components/payment-agreement-settled-state.tsx";
import { formatDate } from "@/utils/currency";
import { useCancelOfflineSubscription } from "@/hooks/payments/useCancelOfflineSubscription";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { StatusChip } from "./PaymentStatusChip";

export const FlowPagePaymentMethod = ({selectedPaymentMethodType, setSelectedPaymentMethodType, ...props}: FlowPageInterface) => {
  const { t, i18n } = useTranslation();
  const { contractId } = useFlow();
  const { 
    data: contract,
    refetch: refetchContract
  } = useContract(contractId);

  const [openDialog, setOpenDialog] = useState(false);
  const [status, setStatus] = useState<"processing" | "error">(
    "processing"
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Picking an off-platform method locks the picker, so a mis-click used to be
  // unrecoverable without phoning the company. The API grants a short window to
  // undo it, once per contract.
  const [confirmChangeOpen, setConfirmChangeOpen] = useState(false);
  const [confirmFinalChoiceOpen, setConfirmFinalChoiceOpen] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);
  // Covers the mutation *and* the contract refetch that follows it. The
  // mutation's own isPending drops the moment the cancel returns, which would
  // re-enable the picker while the contract still says the undo is unspent.
  const [isChangingPaymentMethod, setIsChangingPaymentMethod] = useState(false);
  const cancelOfflineSubscription = useCancelOfflineSubscription();

  const { rows: contractPaymentTypes, loaded: contractPaymentTypesLoaded } =
    useFetchRows("folloportal/contract-payment-types");
  const { rows: paymentPlans, loaded: paymentPlansLoaded, fetch: fetchPaymentPlans } =
    useFetchRows<PaymentPlanResource>(
      `folloportal/payment-plans/available?meta[contract_id]=${contractId}`
    );

  // useFetchRows(force) clears its rows and flips `loaded` back to false for the
  // whole request, so the refetch below would otherwise unmount everything this
  // step renders and mount it again a moment later. Holding on to the previous
  // plans keeps the tab on screen while the refresh is in flight.
  const lastLoadedPlans = useRef<PaymentPlanResource[] | null>(null);
  useEffect(() => {
    if (paymentPlans) {
      lastLoadedPlans.current = paymentPlans;
    }
  }, [paymentPlans]);
  const plansForRender = paymentPlans ?? lastLoadedPlans.current;

  // Every step mounts at page load, so this fetch ran before the customer went
  // through the Contract step. A Smart Quote gets its service (and its real
  // totals) attached THERE — and plan/promotion eligibility is computed from
  // those totals — so re-pull the plans each time this step becomes the active
  // one, or an applicable promotion would stay hidden and the calendar would
  // schedule the full price.
  const wasVisible = useRef(false);
  useEffect(() => {
    if (props.currentlyVisible && !wasVisible.current) {
      fetchPaymentPlans(true);
    }
    wasVisible.current = !!props.currentlyVisible;
  }, [props.currentlyVisible]);

  useEffect(() => {
    // No snapshot, or one naming no method: keep the credit-card default.
    const snapshotPlan = contract?.relationships?.subscriptionActive?.data?.attributes?.payment_snapshot?.paymentPlan;
    const methodKey = snapshotPlan?.selectedPaymentMethodTypeKey;

    if (methodKey) {
      setSelectedPaymentMethodType({
        id: String(snapshotPlan?.selectedPaymentMethodTypeId),
        type: "constants",
        attributes: {
          group: "PAYMENT_METHOD_TYPES",
          key: methodKey,
          value: methodKey
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        },
      });
    }
  }, [contract]);

  useEffect(() => {
    props.setSelectedPaymentPlan(null);
  }, [selectedPaymentMethodType]);

  // The plans are refetched on entry precisely because eligibility (and with it the
  // promotion each plan carries) is computed from totals the Contract step may have
  // just changed. A selection made before that refetch holds the old copy of the
  // plan, so re-point it at the refreshed one — same plan, current promotion.
  useEffect(() => {
    const selectedId = props.selectedPaymentPlan?.id;

    if (!paymentPlans || !selectedId) {
      return;
    }

    const refreshed = paymentPlans.find((plan) => plan.id === selectedId);

    if (refreshed && refreshed !== props.selectedPaymentPlan) {
      props.setSelectedPaymentPlan(refreshed);
    }
  }, [paymentPlans]);

  const services = contract?.relationships?.belongsToManyServices?.data;
  const company = contract?.relationships?.company?.data;
  const eligiblePromotion = props.selectedPaymentPlan?.relationships
  ?.eligiblePromotions?.data?.[0] as PromotionResource | undefined;

  // Only ever true for a plan that offers it: the API refuses it for any other,
  // so the summary must not price on it either.
  const paysInFull = paysInFullNow(props.selectedPaymentPlan, props.payInFull);

  // Keeping the plan's own schedule gives up the discount only when that schedule
  // would pay after the deadline; the summary then prices at full rate instead of
  // going on advertising a rebate the customer just declined. Settling today pays
  // before every deadline, so there is nothing to give up.
  const forfeitsDeadlineDiscount =
    !paysInFull &&
    forfeitsPromotionDiscount(
      eligiblePromotion,
      planFirstPaymentDate(props.selectedPaymentPlan),
      props.paymentTiming,
    );

  const promoPrices = (forfeitsDeadlineDiscount ? undefined : eligiblePromotion) as PromotionResource;

  // Sent on submit so the API applies the promotion the customer was quoted rather
  // than re-picking one, and honours a declined discount.
  const promotionChoicePayload = eligiblePromotion?.id
    ? {
        promotionId: Number(eligiblePromotion.id),
        // Settling today has no payment date left to choose, and sending one would
        // read as a decision the customer was never shown.
        ...(!paysInFull &&
        planMissesPromotionDeadline(
          eligiblePromotion,
          planFirstPaymentDate(props.selectedPaymentPlan),
        )
          ? { paymentTiming: props.paymentTiming }
          : {}),
      }
    : {};

  // Off platform, settling in full is one invoice for everything dated today
  // rather than one per instalment.
  const payInFullPayload = paysInFull ? { payInFull: true } : {};
  const appliedPromo = contract?.meta?.payment_snapshot?.appliedPromotion  
  const subscriptionActiveData = contract?.relationships?.subscriptionActive?.data;
  const hasActiveSubscription = Boolean(subscriptionActiveData?.id);

  const paymentMethodChange = contract?.meta?.payment_method_change;
  // The contract has already spent its one undo, so whatever is picked next is
  // permanent — the customer has to be told before they commit, not after.
  const isFinalPaymentMethodChoice = Boolean(paymentMethodChange?.used);
  // The undo no longer expires on a clock, so `canChange` from the fetched
  // contract is the whole answer — nothing here can go stale while the customer
  // sits on the tab.
  const canChangePaymentMethod =
    Boolean(paymentMethodChange?.canChange) && hasActiveSubscription;

  const isOffPlatformMethodSelected =
    Boolean(selectedPaymentMethodType) && selectedPaymentMethodType?.id !== "500";
  
  // Display prices. The discount comes off the subtotal and the taxes are the
  // ones recalculated on that reduced base, so the summary adds up on screen
  // and matches the contract PDF.
  const priceLadder = buildPriceLadder({
    offer: promoPrices,
    snapshot: appliedPromo,
    prices: contract?.meta?.prices,
  });
  const displayTaxes = promoPrices?.attributes?.taxes ?? appliedPromo?.taxes ?? contract?.meta?.prices?.taxes;
  const displayTotal = promoPrices?.attributes?.new_total ?? contract?.meta?.prices?.total ?? 0;
  const displayBalance = promoPrices?.attributes?.new_balance ?? contract?.meta?.prices?.balance ?? 0;
  // Only the offer currently on the table can carry a "pay before X" warning. The
  // snapshot's promotion is already earned and paid for, and once the customer has
  // declined a deadline discount there is nothing left for a deadline to apply to.
  const promoStopAt = forfeitsDeadlineDiscount
    ? undefined
    : promoPrices?.attributes?.stop_at ?? appliedPromo?.stopAt;
  const promoName = promoPrices?.attributes?.name ?? appliedPromo?.name;
  const discounts = contract?.relationships?.rebates?.data;


  async function subscribeToPlan(params: {
    contractId?: string;
    paymentPlanId?: string;
    payload: OfflineSubscribePayload;
  }) {
    if (!params.paymentPlanId) {
      throw new Error("No payment plan ID");
    }
  
    if (!params.contractId) {
      throw new Error("No contract ID");
    }
  
    const { contractId, paymentPlanId, payload } = params;
    
    return axiosInstance().post(
      `/folloportal/contracts/${contractId}/offline-payment/${paymentPlanId}/subscribe`,
      payload
    );
  }

  const handleConfirmPaymentMethod = async () => {
    if(hasActiveSubscription){
      return props.nextStep();
    }
    if(selectedPaymentMethodType?.id === "500") return props.nextStep();

    // The undo is already spent: say so before the choice is made, since after
    // it there is nothing left to say it about.
    if (isFinalPaymentMethodChoice && isOffPlatformMethodSelected) {
      return setConfirmFinalChoiceOpen(true);
    }

    return submitPaymentMethod();
  };

  const submitPaymentMethod = async () => {
    setConfirmFinalChoiceOpen(false);
    setStatus("processing");
    setOpenDialog(true);
    let resp = null;
    try {
      resp = await subscribeToPlan({
          contractId: contractId,
          paymentPlanId: props.selectedPaymentPlan?.id,
          payload: {
            paymentMethodTypeId: Number(selectedPaymentMethodType?.id),
            ...promotionChoicePayload,
            ...payInFullPayload,
          },
        });
  
      const data = resp.data.data;

  
      if (data.attributes.status === "active") {
        await refetchContract();
        setOpenDialog(false);
        return props.nextStep();
      }

      // Any other status means the subscription did not take. Nothing throws on
      // this path, so without it the customer is left watching the spinner with
      // no error and no way forward.
      setStatus("error");
      setErrorCode(data.attributes.status ?? null);
      return;
    } catch (error) {
      setStatus("error");
      const axiosError = error as AxiosError<{ message: string; errors: Record<string, string[]> }>;
      const message =
        axiosError.response?.data?.errors?.contract?.[0] ??
        axiosError.response?.data?.message ??
        null;
      setErrorCode(message || resp?.data?.errorCode || resp?.data?.reason);
    }
    return;
  };

  const handleChangePaymentMethod = async () => {
    const subscriptionId = subscriptionActiveData?.id;

    if (!contractId || !subscriptionId) {
      return;
    }

    setChangeError(null);
    setIsChangingPaymentMethod(true);

    try {
      await cancelOfflineSubscription.mutateAsync({ contractId, subscriptionId });

      // The undo is spent the moment the cancel lands, but the cached contract
      // still says otherwise, and the hook only *invalidates* it. Wait for the
      // fresh contract before unlocking the picker: on the stale one
      // isFinalPaymentMethodChoice is false, so a customer who re-picks an
      // off-platform method inside that gap is locked into a second irreversible
      // choice with none of the warning this feature exists to give.
      await refetchContract();

      // Start the customer from a clean slate: the agreement they were shown is
      // gone, and leaving the old method selected would re-offer the very choice
      // they just undid.
      setSelectedPaymentMethodType(null);
      props.setSelectedPaymentPlan(null);
      // Eligibility is recomputed from totals the cancellation just changed —
      // the promotion rebate went back with it.
      fetchPaymentPlans(true);
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; errors: Record<string, string[]> }>;

      setChangeError(
        axiosError.response?.data?.errors?.subscription?.[0] ??
          axiosError.response?.data?.message ??
          t("fe.pages.flow.steps.paymentMethod.change.genericError")
      );
    } finally {
      setIsChangingPaymentMethod(false);
      // Closing on the failure path too: changeError renders in the page body,
      // so leaving the dialog up puts its overlay over the only feedback there
      // is and the button just reads as dead.
      setConfirmChangeOpen(false);
    }
  };

  // Still fetching the reference data required to render the tab. A forced
  // refetch counts as "not loaded" and also nulls the rows, so without
  // plansForRender re-entering this step would blank the whole tab for the
  // duration of the round-trip — which is the flicker on entry.
  if (
    !props.currentlyVisible) {
    return <></>;
  }

  // Still fetching the reference data the methods are built from. A forced refetch
  // counts as "not loaded" and also nulls the rows, so without plansForRender
  // re-entering this step would put the whole tab back into loading for the
  // duration of the round-trip — which is the flicker on entry. Only the accordion
  // waits: the card around it, the summary and the contact block are already
  // renderable, so they stay put and the methods fill in underneath them.
  const isLoadingPaymentMethods =
    !contractPaymentTypesLoaded || (!paymentPlansLoaded && !plansForRender);

  // Whether there are still selectable plans for the customer to choose from.
  // When a contract has already been paid/subscribed off-platform (e.g. Interac),
  // the "available" endpoint can legitimately return no plans. In that case we
  // must NOT render a blank page — we show the agreed plan read-only instead.
  const hasSelectablePlans = Boolean(
    contractPaymentTypes && plansForRender && plansForRender.length
  );

  const activeSchedule = subscriptionActiveData?.relationships?.schedule ?? [];
  const activePlanSnapshot =
    subscriptionActiveData?.attributes?.payment_snapshot?.paymentPlan;
  const activeMethodKey =
    activePlanSnapshot?.selectedPaymentMethodTypeKey ??
    selectedPaymentMethodType?.attributes?.key;

  type DialogCfg = Omit<AppDialogProps, "open" | "onOpenChange">;
  const cfgByStatus = (): Record<
    "processing" | "error",
    DialogCfg
  > => ({
    processing: {
      icon: <Loader className="h-15 w-15 animate-spin" />,
      title: t("dialogs.loadingPayment.title"),
      description: t("dialogs.loadingPayment.description"),
    },
    error: {
      icon: <AlertCircle className="h-15 w-15 text-destructive" />,
      title: t("dialogs.paymentError.title"),
      description: t("dialogs.paymentError.description", {
        errorCode: errorCode ?? "400",
      })
    },
  });

  const cfg = cfgByStatus();

  return (
    <>
      <div className="grid lg:grid-cols-3 grid-cols-1 gap-9">
        <div className="col-span-1 md:col-span-2">
          <div className="follo-card">
            <div className="grid gap-3 mx-8 my-7">
              {isLoadingPaymentMethods ? (
                <PaymentMethodAccordionSkeleton />
              ) : hasSelectablePlans && contractId ? (
                <FlowPagePaymentMethodAccordion
                  contractId={contractId}
                  paymentPlans={plansForRender ?? undefined}
                  selectedPaymentMethodType={selectedPaymentMethodType}
                  setSelectedPaymentMethodType={setSelectedPaymentMethodType}
                  selectedPaymentPlan={props.selectedPaymentPlan}
                  setSelectedPaymentPlan={props.setSelectedPaymentPlan}
                  hasActiveSubscription={hasActiveSubscription}
                  subscriptionActive={subscriptionActiveData}
                  appliedPromo={appliedPromo}
                  paymentTiming={props.paymentTiming}
                  setPaymentTiming={props.setPaymentTiming}
                  payInFull={props.payInFull}
                  setPayInFull={props.setPayInFull}
                />
              ) : hasActiveSubscription ? (
                <section>
                  <h3 className="follo-label mb-4">
                    {t("fe.pages.flow.steps.paymentMethod.listOfPaymentsTitle")}
                  </h3>
                  <div className="flex flex-col gap-2 mb-6 text-sm text-card-foreground">
                    {activeMethodKey && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {t("fe.pages.flow.steps.paymentMethod.titleManual", {
                            defaultValue: "Payment method",
                          })}
                        </span>
                        <span className="font-medium">
                          {t(
                            "fe.pages.flow.steps.paymentMethod.paymentMethods." + activeMethodKey,
                            { defaultValue: activeMethodKey.replace(/_/g, " ") }
                          )}
                        </span>
                      </div>
                    )}
                    {activePlanSnapshot?.installments != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {t("fe.pages.flow.steps.paymentMethod.paymentPlansTitle", {
                            defaultValue: "Installments",
                          })}
                        </span>
                        <span className="font-medium">{activePlanSnapshot.installments}</span>
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl border border-muted-foreground/20 overflow-hidden bg-white">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="px-6 text-muted-foreground font-medium text-sm">
                            {t("fe.pages.flow.steps.paymentMethod.table.headings.date")}
                          </TableHead>
                          <TableHead className="px-6 text-muted-foreground font-medium text-sm">
                            {t("fe.pages.flow.steps.paymentMethod.table.headings.amount")}
                          </TableHead>
                          <TableHead className="px-6 text-muted-foreground font-medium text-sm">
                            {t("fe.pages.flow.steps.paymentMethod.table.headings.status")}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeSchedule.length > 0 ? (
                          activeSchedule.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                                {formatDate(item.attributes.date, i18n.language)}
                              </TableCell>
                              <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                                {formatCurrency(Number(item.attributes.amount), i18n.language)}
                              </TableCell>
                              <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                                {item.relationships.invoice ? (
                                  <StatusChip label={t("fe.pages.flow.steps.payment.statusPayed")} variant="paid" />
                                ) : (
                                  <StatusChip label={t("fe.pages.flow.steps.payment.statusScheduled")} variant="scheduled" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={3}
                              className="px-6 h-20 text-center text-sm text-muted-foreground"
                            >
                              {t("fe.pages.flow.steps.paymentMethod.table.emptyState")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </section>
              ) : contract?.meta?.prices?.balance === 0 ? (
                <PaymentAgreementSettledState />
              ) : (
                <PaymentAgreementEmptyState />
              )}

              {canChangePaymentMethod && (
                <section className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-card-foreground">
                    <p className="font-medium">
                      {t("fe.pages.flow.steps.paymentMethod.change.title")}
                    </p>
                    <p className="text-muted-foreground">
                      {t("fe.pages.flow.steps.paymentMethod.change.description")}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setChangeError(null);
                      setConfirmChangeOpen(true);
                    }}
                    className="cursor-pointer shrink-0"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    {t("fe.pages.flow.steps.paymentMethod.change.action")}
                  </Button>
                </section>
              )}

              {changeError && (
                <p className="text-sm text-destructive" role="alert">
                  {changeError}
                </p>
              )}

              {!hasActiveSubscription && isFinalPaymentMethodChoice && isOffPlatformMethodSelected && (
                <p className="text-sm text-destructive flex items-start gap-2" role="alert">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{t("fe.pages.flow.steps.paymentMethod.change.finalChoiceNotice")}</span>
                </p>
              )}
            </div>
            {isLoadingPaymentMethods && (
              <>
                <Separator />
                <div className="flex w-full justify-end py-4 px-7">
                  <Skeleton className="h-9 w-28 rounded-md" />
                </div>
              </>
            )}
            {!isLoadingPaymentMethods && (hasSelectablePlans || hasActiveSubscription) && (
              <>
                <Separator />
                <div className="flex w-full justify-end py-4 px-7">
                  <Button
                    // Green while it commits to the plan; once subscribed it only moves on.
                    variant={hasActiveSubscription ? "default" : "success"}
                    size={hasActiveSubscription ? "default" : "lg"}
                    disabled={
                      !hasActiveSubscription &&
                      (selectedPaymentMethodType === null ||
                        props.selectedPaymentPlan === null)
                    }
                    onClick={handleConfirmPaymentMethod}
                    className="cursor-pointer"
                  >
                    {hasActiveSubscription || selectedPaymentMethodType?.id === "500" ? t("ui.next") : t("ui.confirm")}
                  </Button>
                </div>
              </>
            )}
          </div>
          <ContactUs email={company?.attributes.email} />
        </div>
        <div className="col-span-1">
          <div className="follo-card px-8 py-7">
            <section>
              <h3 className="follo-label mb-4.5">
                {t("fe.pages.flow.steps.paymentMethod.title2")}
              </h3>
              {services && (
                <>
                  {services.map((service, index) => {
                    return (
                      <div
                        key={index + "_" + service.id}
                        className={
                          "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
                        }
                      >
                        <div>{service.attributes.name}</div>
                        <div>{formatCurrency(service.meta.pivot.price ?? 0, i18n.language)}</div>
                      </div>
                    );
                  })}
                </>
              )}
              <div
                className={
                  "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-4"
                }
              >
                <div>{t("fe.pages.flow.steps.summary.details.subTotal")}</div>
                <div>{formatCurrency(priceLadder.subtotal ?? 0, i18n.language)}</div>
              </div>
              {discounts && discounts.length > 0 && (
                <>
                  <div className={"text-sm leading-5 font-medium text-card-foreground pb-3.5"}>
                    {t("fe.pages.flow.steps.summary.details.discounts")}
                  </div>
                  {discounts.map((discount, index) => {
                    const discountName = localizedRebateTypeName(discount, i18n.language);

                    return (
                      <div
                        key={index + "_" + discount.id}
                        className={
                          "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-4"
                        }
                      >
                        <div className="max-w-2/3">
                          <div>{discountName ?? discount.attributes.note ?? "-"}</div>
                          {discountName && discount.attributes.note && (
                            <div className="text-xs leading-4 text-muted-foreground">
                              {discount.attributes.note}
                            </div>
                          )}
                        </div>
                        <div>{formatCurrency(-(discount.attributes.price ?? 0), i18n.language)}</div>
                      </div>
                    );
                  })}
                </>
              )}
              {priceLadder.hasPromotion && (
                <div className="flex flex-col gap-1 pb-4 text-[#0A4DBD]">
                  <div className="flex justify-between text-sm leading-5 font-normal">
                    <span>
                      {t("fe.pages.flow.steps.summary.details.promotion", {
                        defaultValue: "Promotion",
                      })}
                    </span>
                    <span>
                      {formatCurrency(-priceLadder.discount, i18n.language)}
                    </span>
                  </div>
                  {promoName && (
                    <div className="text-xs leading-4 font-medium">
                      {promoName}
                    </div>
                  )}
                  {promoStopAt && (
                    <div className="text-xs leading-4 font-normal">
                      {t("fe.pages.flow.steps.paymentMethod.promoValidUntil", {
                        date: formatDate(promoStopAt, i18n.language),
                        defaultValue: "Discount valid only if paid before {{date}}",
                      })}
                    </div>
                  )}
                </div>
              )}
              {priceLadder.isDiscounted && priceLadder.netSubtotal != null && (
                <div
                  className={
                    "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-4"
                  }
                >
                  <div>
                    {t("fe.pages.flow.steps.summary.details.netSubTotal", {
                      defaultValue: "Net subtotal",
                    })}
                  </div>
                  <div>{formatCurrency(priceLadder.netSubtotal, i18n.language)}</div>
                </div>
              )}
              {displayTaxes?.taxes?.map((tax) => {
                return (
                  <div
                    key={tax.code}
                    className={
                      "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-4"
                    }
                  >
                    <div>{tax.label}</div>
                    <div>{formatCurrency(tax.amount, i18n.language)}</div>
                  </div>
                )
              })}
              {displayBalance < displayTotal && (
                <div
                  className={
                    "flex justify-between text-sm leading-5 font-medium text-destructive pb-4"
                  }
                >
                  <div className="font-semibold">
                    {t("fe.pages.flow.steps.payment.balanceLeft")}
                  </div>
                  <div className={"text-base leading-6 font-semibold"}>
                    {formatCurrency(displayBalance ?? 0, i18n.language)}
                  </div>
                </div>
              )}
              <div
                className={
                  "flex justify-between text-sm leading-5 font-medium text-primary pb-4"
                }
              >
                <div className="font-semibold">
                  {t("fe.pages.flow.steps.paymentMethod.totalPerSeason")}
                </div>
                <div className={"text-base leading-6 font-semibold"}>
                  {formatCurrency(displayTotal ?? 0, i18n.language)}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <AppDialog open={openDialog} onOpenChange={setOpenDialog} {...cfg[status]} />

      <AppDialog
        open={confirmChangeOpen}
        onOpenChange={setConfirmChangeOpen}
        icon={<RefreshCcw className="h-15 w-15 text-primary" />}
        title={t("fe.pages.flow.steps.paymentMethod.change.confirmTitle")}
        description={t("fe.pages.flow.steps.paymentMethod.change.confirmDescription")}
        actions={[
          {
            label: isChangingPaymentMethod
              ? t("ui.loading")
              : t("fe.pages.flow.steps.paymentMethod.change.confirmAction"),
            onClick: handleChangePaymentMethod,
            disabled: isChangingPaymentMethod,
          },
          {
            label: t("ui.cancel"),
            onClick: () => setConfirmChangeOpen(false),
            disabled: isChangingPaymentMethod,
            variant: "outline",
          },
        ]}
      />

      <AppDialog
        open={confirmFinalChoiceOpen}
        onOpenChange={setConfirmFinalChoiceOpen}
        icon={<AlertCircle className="h-15 w-15 text-destructive" />}
        title={t("fe.pages.flow.steps.paymentMethod.change.finalChoiceTitle")}
        description={t("fe.pages.flow.steps.paymentMethod.change.finalChoiceDescription")}
        actions={[
          {
            label: t("fe.pages.flow.steps.paymentMethod.change.finalChoiceAction"),
            onClick: submitPaymentMethod,
          },
          {
            label: t("ui.cancel"),
            onClick: () => setConfirmFinalChoiceOpen(false),
            variant: "outline",
          },
        ]}
      />
    </>
  );
};
